import React, { useMemo } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  DataTableContainer,
  ACTIONS_COLUMN_ICON,
  ACTIONS_VIEW_ICON_SX,
  ColumnsToolbarButton,
  ResetLayoutButton,
  FilteredColumnsPanel,
  STANDARD_GRID_PROPS,
  STANDARD_COLUMN_HEADER_TITLE_SX,
  DataGridActionsLinkButton,
} from "@app.automotus.io/components/DataTableContainer";
import { useSearchParams } from "react-router-dom";
import {
  DataGridPro,
  getGridStringOperators,
  GridActionsColDef,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterInputValueProps,
  GridFilterModel,
  GridSortModel,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { useQuery } from "@apollo/client";
import { TableErrorOverlay } from "@app.automotus.io/components/PayeeTables/TableErrorOverlay";
import { TableLoadingOverlay } from "@app.automotus.io/components/PayeeTables/TableLoadingOverlay";
import { formatCurrency } from "common/format";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import format from "date-fns/format";
import {
  CitationOrderBy,
  CitationSummary,
  GET_CITATIONS,
  GetCitationsData,
  GetCitationsVars,
  GetCitationsWhereFilter,
} from "common/graphql";
import {
  parseSortModel,
  serializeSortModel,
  parseFilterModelFromSearchParams,
  filterModelToSearchParams,
} from "common/graphql/queryString";
import { getViolationTypeLabel, getViolationTypeOptions } from "common/format/violationType";
import { Theme } from "@mui/material/styles";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { useApplicableViolationTypes, useUserProfile } from "@app.automotus.io/components/hooks";

export interface CitationsLocationState {
  citationTablePage?: number;
  citationTablePageSize?: number;
  citationTableSortStr?: string;
}

export const Citations: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    page: "0",
    pageSize: "50",
  });
  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    searchParams.set("page", `${newPage}`);
    setSearchParams(searchParams);
  };

  const pageSize = Number(searchParams.get("pageSize") || 50);
  const handlePageSizeChange = (newPageSize: number) => {
    searchParams.set("pageSize", `${newPageSize}`);
    setSearchParams(searchParams);
  };

  const sortStr = searchParams.get("sort") || "noticeDate+desc";
  const sortModel: GridSortModel = parseSortModel(sortStr);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    setSearchParams(searchParams);
  };

  // Carried on each row's detail link so its own back button can restore this exact list view
  // even when the link is opened in a new tab, where react-router's `state` (passed separately
  // below) never arrives: a fresh tab has no access to the originating tab's in-memory history
  // state, only to whatever is in the URL itself.
  const returnTo = `/dashboard/citations?${searchParams.toString()}`;

  return (
    <Stack spacing={3} width={"100%"} sx={{ height: "100%", flexGrow: 1 }}>
      <Box>
        <Typography variant={"h4"} fontWeight={600} letterSpacing={0.25}>
          Citations
        </Typography>
        <Typography variant={"subtitle1"} fontSize={"12px"} letterSpacing={0.4} color={"text.disabled"}>
          Recent transactions can take up to 24 hours to process.
        </Typography>
      </Box>
      <CitationTable
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        locationState={{ citationTablePage: page, citationTablePageSize: pageSize, citationTableSortStr: sortStr }}
        returnTo={returnTo}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />
    </Stack>
  );
};

const CITATION_TABLE_DEFAULT_VISIBILITY: GridColumnVisibilityModel = {
  citationNumber: true,
  licensePlate: true,
  violationType: true,
  noticeDate: true,
  addressStreet: true,
  paidDate: true,
  status: true,
  amount: true,
  actions: true,
};

export const CitationTableToolbar: React.FC<CitationTableToolbarProps> = ({
  exportFileName,
  defaultVisibilityModel,
  isIconOnly,
  resetLayout,
}) => {
  const apiRef = useGridApiContext();
  return (
    <Stack
      direction={"row"}
      justifyContent={"flex-start"}
      alignItems={"center"}
      height={52}
      px={1.25}
      bgcolor={(theme: Theme) => theme.palette.background.paper}
      spacing={1}
    >
      <GridToolbarFilterButton
        componentsProps={{
          button: {
            sx: {
              fontSize: "16px",
              fontWeight: 400,
              letterSpacing: 0.15,
              textTransform: "none",
              ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
            },
          },
        }}
      />
      <ColumnsToolbarButton apiRef={apiRef} defaultVisibilityModel={defaultVisibilityModel} />
      <GridToolbarExport
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{ fileName: exportFileName }}
        excelOptions={{ fileName: exportFileName }}
        sx={{
          fontSize: "16px",
          fontWeight: 400,
          letterSpacing: 0.15,
          textTransform: "none",
          ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
        }}
      />
      <ResetLayoutButton onReset={resetLayout} isIconOnly={isIconOnly} />
    </Stack>
  );
};

const CitationStatusFilterValue: React.FC<GridFilterInputValueProps> = ({ item, applyValue }) => {
  const handleChange = (event: SelectChangeEvent) => {
    applyValue({ ...item, value: event.target.value as string });
  };

  return (
    <FormControl variant="standard">
      <InputLabel id="status-filter-select-label">Status</InputLabel>
      <Select
        labelId="status-filter-select-label"
        id="status-filter-select"
        value={item.value || ""}
        label="Status"
        onChange={handleChange}
      >
        <MenuItem value={""}>Any</MenuItem>
        <MenuItem value={"Paid"}>Paid</MenuItem>
        <MenuItem value={"Unpaid"}>Unpaid</MenuItem>
        <MenuItem value={"Overdue"}>Overdue</MenuItem>
        <MenuItem value={"Sent to Collections"}>Sent to Collections</MenuItem>
        <MenuItem value={"Void"}>Void</MenuItem>
      </Select>
    </FormControl>
  );
};

interface ViolationTypeFilterValueProps extends GridFilterInputValueProps {
  applicableViolationTypes?: string[];
  accountId?: string;
}

const ViolationTypeFilterValue: React.FC<ViolationTypeFilterValueProps> = ({
  item,
  applyValue,
  applicableViolationTypes,
  accountId,
}) => {
  const options = useMemo(
    () => getViolationTypeOptions(accountId, applicableViolationTypes),
    [accountId, applicableViolationTypes],
  );

  const handleChange = (event: SelectChangeEvent) => {
    applyValue({ ...item, value: event.target.value as string });
  };

  return (
    <FormControl variant="standard">
      <InputLabel id="violation-type-filter-select-label">Violation Type</InputLabel>
      <Select
        labelId="violation-type-filter-select-label"
        id="violation-type-filter-select"
        value={item.value || ""}
        label="Violation Type"
        onChange={handleChange}
      >
        <MenuItem value={""}>Any</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export interface CitationTableToolbarProps {
  exportFileName: string;
  /** The table's default column visibility — see useHiddenColumnsCount's docs on why this must stay a stable, code-defined constant. */
  defaultVisibilityModel: GridColumnVisibilityModel;
  /** Below 700px, collapses the filter/export buttons to icon-only (their labels are already blanked via the parent DataGridPro's `localeText`) and tightens their icon spacing to match. */
  isIconOnly: boolean;
  /** Clears this table's saved layout and restores its defaults — see DataTableContainerChildrenParams.resetLayout. */
  resetLayout: () => void;
}

// Extracted from onFilterChange so the same transform can also hydrate `filter` from a
// URL-restored GridFilterModel on mount (see parseFilterModelFromSearchParams below) — otherwise
// a filtered view's detail-page back link (returnTo) would round-trip page/pageSize/sort but land
// back on an unfiltered table, since nothing would re-derive the GraphQL filter from the URL.
const buildCitationsFilter = (filterModel: GridFilterModel): GetCitationsWhereFilter => {
  return filterModel.items.reduce(
    (acc, item) => {
      if (item.columnField === "citationNumber") {
        return { ...acc, citation_number: { _eq: item.value } };
      }

      if (item.columnField === "licensePlate") {
        return { ...acc, session_transaction: { registered_vehicle: { license_plate: { _eq: item.value } } } };
      }

      if (item.columnField === "status") {
        switch (item.value) {
          case "Paid":
            return { ...acc, paid_at: { _is_null: false } };
          case "Unpaid":
            return { ...acc, paid_at: { _is_null: true }, voided_at: { _is_null: true } };
          case "Void":
            return { ...acc, voided_at: { _is_null: false } };
          case "Overdue":
            return {
              ...acc,
              voided_at: { _is_null: true },
              paid_at: { _is_null: true },
              due_at: { _lt: new Date() },
            };
          case "Sent to Collections":
            return { ...acc, sent_to_collections_at: { _is_null: false } };
          default:
            return acc;
        }
      }

      if (item.columnField === "violationType") {
        return { ...acc, violation_type: { _eq: item.value } };
      }

      return acc;
    },
    { notice_sent_at: { _is_null: false } } as GetCitationsWhereFilter,
  );
};

export const CitationTable: React.FC<CitationTableProps> = ({
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  locationState,
  returnTo,
  sortModel,
  onSortModelChange,
}) => {
  const { applicableViolationTypes } = useApplicableViolationTypes("citations", {
    notice_sent_at: { _is_null: false },
  });
  const { userProfile } = useUserProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  // Restores a filter from the URL on mount — including when landing here via a detail page's
  // `returnTo` back link, or a plain refresh/bookmark — so the table isn't silently unfiltered.
  const appliedFilterModel = useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const CITATION_TABLE_COLUMNS = useMemo(
    () => [
      {
        field: "citationNumber",
        headerName: "Citation ID",
        flex: 1,
        align: "left",
        headerAlign: "left",
        sortingOrder: ["desc", "asc"],
        filterable: true,
        filterOperators: getGridStringOperators().filter((operator) => operator.value === "equals"),
      } as GridColDef<CitationSummary, string>,
      {
        field: "licensePlate",
        headerName: "License Plate",
        flex: 1,
        valueGetter({ row }) {
          return row?.sessionTransaction?.registeredVehicle
            ? `${row.sessionTransaction.registeredVehicle.licensePlateStateCode} ${row.sessionTransaction.registeredVehicle.licensePlateNumber}`
            : "-";
        },
        sortable: false,
        filterOperators: getGridStringOperators().filter((operator) => operator.value === "equals"),
      } as GridColDef<CitationSummary, string>,
      {
        field: "violationType",
        headerName: "Violation Type",
        flex: 1,
        sortable: false,
        valueFormatter({ value }) {
          return getViolationTypeLabel(value, userProfile?.account?.id);
        },
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((operator) => operator.value === "equals")
          .map((operator) => ({
            ...operator,
            InputComponent: operator.InputComponent ? ViolationTypeFilterValue : undefined,
            InputComponentProps: { applicableViolationTypes, accountId: userProfile?.account?.id },
          })),
      } as GridColDef<CitationSummary, string>,
      {
        field: "noticeDate",
        headerName: "Notice Date",
        type: "date",
        flex: 1,
        valueGetter({ row }) {
          return new Date(row.noticeSentAt);
        },
        sortingOrder: ["desc", "asc"],
        filterable: false,
      } as GridColDef<CitationSummary, Date>,
      {
        field: "addressStreet",
        headerName: "Address",
        valueGetter({ row }) {
          return row.sessionTransaction.addressStreet ?? "-";
        },
        filterable: false,
      } as GridColDef<CitationSummary, string>,
      {
        field: "paidDate",
        headerName: "Paid Date",
        type: "date",
        flex: 1,
        valueGetter({ row }) {
          return row.paidAt ? new Date(row.paidAt) : null;
        },
        valueFormatter({ value }) {
          return value ? format(value, "MM/dd/yyyy") : "-";
        },
        sortingOrder: ["desc", "asc"],
        filterable: false,
      } as GridColDef<CitationSummary, Date | null, string>,
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        valueGetter({ row }) {
          if (row.paidAt) {
            return "Paid";
          } else if (row?.dispute?.status === "pending") {
            return "Under Review";
          } else if (row.voidedAt) {
            return "Void";
          } else if (new Date(row.dueAt).getTime() < Date.now()) {
            return "Overdue";
          } else {
            return "Pending";
          }
        },
        sortable: false,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((operator) => operator.value === "equals")
          .map((operator) => ({
            ...operator,
            InputComponent: operator.InputComponent ? CitationStatusFilterValue : undefined,
          })),
      } as GridColDef<CitationSummary, string>,
      {
        field: "amount",
        headerName: "Amount",
        flex: 1,
        sortable: false,
        valueGetter({ row }) {
          return row.totalAmountDue;
        },
        valueFormatter({ value }) {
          return `$${formatCurrency(value)}`;
        },
        filterable: false,
      } as GridColDef<CitationSummary, number, string>,
      {
        ...ACTIONS_COLUMN_ICON,
        getActions: (params) => [
          <DataGridActionsLinkButton
            to={`${params.row.id}?returnTo=${encodeURIComponent(returnTo)}`}
            state={locationState}
            label={"View Citation"}
            icon={<ArrowForwardIosIcon sx={ACTIONS_VIEW_ICON_SX} />}
          />,
        ],
      } as GridActionsColDef,
    ],
    [locationState, returnTo, applicableViolationTypes, userProfile?.account?.id],
  );
  const offset = page * pageSize;
  const limit = pageSize;

  const parseOrderBy = (sortModel: GridSortModel): CitationOrderBy[] => {
    const { field: sortField, sort: sortDirection } = sortModel[0];
    switch (sortField) {
      case "citationNumber":
        return [{ citation_number: sortDirection || "asc" }];
      case "paidDate":
        return [{ paid_at: sortDirection === "asc" ? "asc_nulls_last" : "desc_nulls_last" }];
      default:
        return [{ notice_sent_at: sortDirection || "desc" }];
    }
  };

  const [filter, setFilter] = React.useState<GetCitationsWhereFilter>(() =>
    appliedFilterModel ? buildCitationsFilter(appliedFilterModel) : { notice_sent_at: { _is_null: false } },
  );
  const onFilterChange = React.useCallback(
    (newFilterModel: GridFilterModel) => {
      setFilter(buildCitationsFilter(newFilterModel));
      setSearchParams(filterModelToSearchParams(newFilterModel, searchParams), { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const { data, loading, error, refetch } = useQuery<GetCitationsData, GetCitationsVars>(GET_CITATIONS, {
    variables: {
      offset,
      limit,
      orderBy: parseOrderBy(sortModel),
      filter,
    },
    onError(err) {
      console.error("failed to fetch citations", err);
    },
  });
  const exportFileName = `citations_${(Math.random() * 1e6).toFixed(0)}`;

  return (
    <DataTableContainer tableName="citations-table" defaultVisibilityModel={CITATION_TABLE_DEFAULT_VISIBILITY}>
      {({ apiRef, initialState, isIconOnly, resetLayout }) => (
        <DataGridPro
          apiRef={apiRef}
          localeText={isIconOnly ? { toolbarFilters: "", toolbarExport: "" } : undefined}
          components={{
            ErrorOverlay: TableErrorOverlay,
            LoadingOverlay: TableLoadingOverlay,
            Toolbar: CitationTableToolbar,
            ColumnsPanel: FilteredColumnsPanel,
          }}
          componentsProps={{
            toolbar: {
              exportFileName: exportFileName,
              defaultVisibilityModel: CITATION_TABLE_DEFAULT_VISIBILITY,
              isIconOnly,
              resetLayout,
            },
            errorOverlay: {
              onTryAgain: () => refetch(),
            },
            loadingOverlay: {
              numColumns: CITATION_TABLE_COLUMNS.length,
            },
          }}
          columns={CITATION_TABLE_COLUMNS}
          rows={data?.citations || []}
          rowCount={data?.citationsAggregate.aggregate.count || 0}
          loading={loading}
          {...STANDARD_GRID_PROPS}
          error={error}
          pagination
          page={page}
          onPageChange={onPageChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          paginationMode={"server"}
          rowsPerPageOptions={[50, 100]}
          sortingMode={"server"}
          sortModel={sortModel}
          filterMode={"server"}
          onFilterModelChange={onFilterChange}
          initialState={
            appliedFilterModel ? { ...initialState, filter: { filterModel: appliedFilterModel } } : initialState
          }
          onSortModelChange={onSortModelChange}
          sx={{
            flexGrow: 1,
            ...STANDARD_COLUMN_HEADER_TITLE_SX,
          }}
        />
      )}
    </DataTableContainer>
  );
};

export interface CitationTableProps {
  page: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  locationState: CitationsLocationState;
  /** Relative URL of this list view (path + current page/pageSize/sort/filter), for the detail page's back link. */
  returnTo: string;
  sortModel: GridSortModel;
  onSortModelChange: (sortModel: GridSortModel) => void;
}

export default Citations;
